import FormInput from 'components/FormInput/FormInput';
import CenterFormLayout from 'components/Layout/centerFormLayout';
import styles from '../report.module.scss';
import { Form, Formik } from 'formik';
import React, { useEffect, useRef, useState } from 'react';
import Button from 'components/Button/Button';
import EditDiagnosticNotes from './editDiagnosticNotes';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  deleteDiagnosticFindingAction,
  editDiagnosticFindingsAction,
  getFindingsPerProcedureAction,
  getNotesPerFindingsAction
} from 're-ducks/report';
import { toast } from 'react-toastify';
import { removeEmpty } from 'utils/removeEmptyValues';
import { CreateFindingsSchema } from 'utils/validation';
import classNames from 'classnames';
import Select from 'components/Select';
import AddFindingsModal from './addFindingsModal';
import Close from 'assets/icons/close.svg';
import DeleteFindingModal from './deleteFinding';

const EditDiagnosticFindings = () => {
  const [findings, setFindings] = useState([]);
  const [findingsNotes, setFindingNotes] = useState({});
  const [findingsId, setFindingsId] = useState('');
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState('');
  const [isDeleteLoading, setIsDeleteLoading] = useState('');
  const params = useParams();
  const stepOneRef = useRef(null);
  const [stepTwoResetKey, setStepTwoResetKey] = useState(0);
  const navigate = useNavigate();
  const [active, setActive] = useState({});
  const [selectedReportType, setSelectedReportType] = useState({});
  const [addNewFindings, setAddNewFindings] = useState(false);
  const [isFetchingNotes, setIsFetchingNotes] = useState(false);
  const [openFindings, setOpenFindings] = useState(false);
  const [openModal, setOpenModal] = useState(false);

  const resetAll = () => {
    setFindingsId(null);

    dispatch({
      type: 'SET_SINGLE_REPORT',
      payload: null
    });

    if (stepOneRef.current) {
      stepOneRef.current.resetForm();
    }

    setStepTwoResetKey((prev) => prev + 1);
  };

  const reportTypes = [
    { name: 'Personal report', id: 'Personal report' },
    { name: 'Corporate report', id: 'Corporate report' }
  ];

  const commaSeperatedValues = (data) => {
    return data
      ?.split('\n')
      ?.filter((item) => item.trim() !== '')
      ?.join(',');
  };

  const handleEditDiagnosticFindings = (values) => {
    const data = removeEmpty(values);
    const res = {
      pick_checkers: commaSeperatedValues(values.pick_checkers)
    };
    const others = removeEmpty(res);

    setIsLoading(true);
    dispatch(
      editDiagnosticFindingsAction(
        {
          id: active?.id,
          params: {
            ...data,
            ...others
          }
        },
        (res) => {
          setIsLoading(false);
          toast.success(res.message);
          setFindingsId(res?.data?.[0]?.id);
        },
        (err) => {
          setIsLoading(false);
          toast.error(err);
        }
      )
    );
  };

  useEffect(() => {
    if (active) {
      setFindingsId(active.id);
      setIsFetchingNotes(true);
      dispatch(
        getNotesPerFindingsAction(
          {
            test_id: params?.code,
            findings_id: active?.id
          },
          (res) => {
            setIsFetchingNotes(false);
            setFindingNotes(res.data);
          },
          (err) => {
            setIsFetchingNotes(false);
            toast.error(err);
          }
        )
      );
    }
  }, [active]);

  const handleClose = () => {
    dispatch({
      type: 'SET_SINGLE_REPORT',
      payload: null
    });
    return navigate(-1);
  };

  const activeClass = (tab) => tab?.id === active?.id && styles.active;

  const currentFindings = findings?.filter((i) => i.id === active.id);

  const fetchFindings = () => {
    dispatch(
      getFindingsPerProcedureAction(
        { test_id: params?.code },
        (res) => {
          setFindings(res?.data);
          setActive(res?.data?.[0] || {});
        },
        (err) => toast.error(err)
      )
    );
  };
  useEffect(() => {
    fetchFindings();
  }, [params?.code]);

  const handleDelete = () => {
    setIsDeleteLoading(true);
    dispatch(
      deleteDiagnosticFindingAction(
        {
          id: findingsId
        },
        (res) => {
          setIsDeleteLoading(false);
          toast.success(res.message);
          setOpenModal(false);
          fetchFindings();
        },
        (err) => {
          toast.error(err);
        }
      )
    );
  };
  return (
    <div>
      <CenterFormLayout
        backTheme="tertiary"
        backSize="sm"
        closeTheme="tertiary"
        closeSize="sm"
        onBackClick={handleClose}
        title="Edit Diagnostic Findings"
        onClose={handleClose}>
        <>
          <div
            style={{
              width: '90%',
              display: 'flex',
              justifyContent: 'flex-end'
            }}>
            <Button theme="primary" onClick={() => setOpenFindings(true)}>
              Add new findings
            </Button>
          </div>
          <div className={styles.create_diagnostics__edit}>
            <ul className={styles.create_diagnostics__edit__findings}>
              {findings?.map((item, index) => (
                <li
                  key={index}
                  className={classNames(activeClass(item))}
                  onClick={() => setActive(item)}>
                  <span className={styles.finding_number}>{index + 1}</span>
                  <span className={styles.finding_text}>{item?.diagnostic_findings}</span>
                  <img src={Close} alt="delete" onClick={() => setOpenModal(true)} />
                </li>
              ))}
            </ul>
            <div className={styles.create_diagnostics__edit__form}>
              <Formik
                initialValues={{
                  title: currentFindings?.[0]?.diagnostic_findings,
                  description: currentFindings?.[0]?.description ?? '',
                  note_tied_to: currentFindings?.[0]?.report_type ?? '',
                  pick_checkers: currentFindings?.[0]?.pick_checker?.join('\n') ?? ''
                }}
                enableReinitialize
                innerRef={stepOneRef}
                onSubmit={(values) => {
                  handleEditDiagnosticFindings(values);
                }}
                validationSchema={CreateFindingsSchema}>
                {(formik) => {
                  return (
                    <Form>
                      <div className={styles.create_diagnostics__form__input}>
                        <Select
                          label="Report Type"
                          placeholder={formik.values.note_tied_to ?? ''}
                          options={reportTypes}
                          name="note_tied_to"
                          selected={selectedReportType}
                          handleChange={(e) => {
                            formik.setFieldValue('note_tied_to', e.name);
                            setSelectedReportType(e);
                          }}
                        />
                        {formik.errors.note_tied_to ? (
                          <span className="error">{formik.errors.note_tied_to}</span>
                        ) : null}
                      </div>
                      <div className={styles.create_diagnostics__form__input}>
                        <FormInput
                          label="Title"
                          type="text"
                          value={formik.values.title}
                          onChange={formik.handleChange}
                          name="title"
                        />
                        {formik.errors.title ? (
                          <span className="error">{formik.errors.title}</span>
                        ) : null}
                      </div>
                      <div className={styles.create_diagnostics__textarea}>
                        <p>Description</p>
                        <textarea
                          name="description"
                          value={formik.values.description}
                          onChange={formik.handleChange}
                        />
                      </div>
                      <div className={styles.create_diagnostics__textarea}>
                        <p>Pick Checkers</p>
                        <textarea
                          name="pick_checkers"
                          value={formik.values.pick_checkers}
                          onChange={formik.handleChange}
                        />
                      </div>
                      <div className={styles.create_diagnostics__continueBtn}>
                        <Button theme="primary" type="submit" loading={isLoading}>
                          Save & Continue
                        </Button>
                      </div>
                    </Form>
                  );
                }}
              </Formik>
              <EditDiagnosticNotes
                isDisabled={!findingsId}
                findingsId={findingsId}
                key={stepTwoResetKey}
                resetAll={resetAll}
                findingsNotes={findingsNotes}
                currentFindings={currentFindings}
                setAddNewFindings={setAddNewFindings}
                isFetchingNotes={isFetchingNotes}
              />
              <AddFindingsModal
                setAddNewFindings={setAddNewFindings}
                setFindings={setFindings}
                edit
                isOpen={openFindings}
                isClose={() => setOpenFindings(false)}
              />
              {openModal && (
                <DeleteFindingModal
                  isOpen={openModal}
                  isClose={() => setOpenModal(false)}
                  onDelete={handleDelete}
                  loadingBtn={isDeleteLoading}
                />
              )}
            </div>
          </div>
        </>
      </CenterFormLayout>
    </div>
  );
};

export default EditDiagnosticFindings;
